import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  formatDate,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StudentEnrollment,
  StudentEnrollmentBulkUpdate,
  StudentEnrollmentBulkUpdateSchema,
  StudentEnrollmentEditResource,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { EnrollmentUpdateSchema } from "./EnrollmentUpdateSchema";

const routeName = "studentEnrollmentBulkUpdate";

export const studentEnrollmentBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Enrollments",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentEnrollmentBulkUpdatePageState = {
  selectedIds: number[];
};

export const StudentEnrollmentBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentEnrollmentBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<StudentEnrollmentBulkUpdate>({});
  const [enrollments, setEnrollments] = useState<Map<number, StudentEnrollment>>(new Map());
  const [resource, setResource] = useState<StudentEnrollmentEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.studentEnrollments.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      setEnrollments(new Map(response.enrollments?.map((x) => [x.id, x])));
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.studentEnrollments.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkUpdate: StudentEnrollmentBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.studentEnrollments
      .saveBulkUpdate({ ...bulkUpdate, enrollments: Array.from(enrollments.values()) })
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentEnrollmentBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate?.enrollments && bulkUpdate?.enrollments?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <EnrollmentUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as StudentEnrollmentBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<StudentEnrollmentBulkUpdate>("updateSchema")] || []}
        />

        <BulkSelected
          label="Enrollment"
          items={enrollments || []}
          setItems={(value) => setEnrollments(value)}
          getItemLabel={(enrollment) => (
            <Stack>
              <Typography>
                {enrollment.student?.lastName}, {enrollment.student?.firstName}
              </Typography>
              <Typography variant="caption" lineHeight="100%">
                {formatDate(enrollment.startDate)}
                {enrollment.endDate ? <> - {formatDate(enrollment.endDate)}</> : ""}
              </Typography>
            </Stack>
          )}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
